import React, { useState, useEffect, useCallback, useContext, useMemo } from "react";
import styled from "@emotion/styled";
import { Helmet } from "react-helmet-async";
import { DataGrid, GridFooter, GridToolbar } from "@mui/x-data-grid";
// import AddClaimForm from "../../components/AddClaimForm";
import {
  Grid,
  Box,
  Card as MuiCard,
  Typography,
  Divider,
  IconButton,
  CircularProgress,
  Stack,
  Tabs,
  Tab,
  Tooltip,
  Button,
  // Icon,
} from "@mui/material";
// import InfoIcon from "@mui/icons-material/Info";
// import { Delete as DeleteIcon } from "@mui/icons-material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { spacing } from "@mui/system";
import Notification from "../../components/Notification";
import ConfirmDialog from "../../components/ConfirmDialog";
import axios from "axios";
import ViewClaimDialog from "../../components/ViewClaimDialog";
import ClaimPayoutDetailsDialog from "../../components/ClaimPayoutDetailsDialog";
import DataGridExport from "../../components/DataGridExport";
// import { useMsal } from "@azure/msal-react";
import InfoIcon from "@mui/icons-material/InfoOutlined";
// import { ErrorMessage } from "formik";
import { GlobalContext } from "../../contexts/GlobalContext";
import SelectWrapper from "../../components/SelectBoxDataGridCell";
import { useTranslation } from "react-i18next";
import {
  claimCodeEndsWithZero,
  getVatClaimPayoutDateSortValue,
  resolveVatClaimPayoutDate,
} from "../../utils/claimPayoutDateUtils";
import { formatDisplayDate } from "../../utils/dateFormatUtils";
import { useDataGridLocaleText } from "../../utils/useDataGridLocaleText";

const Card = styled(MuiCard)(spacing);

const CustomerClaims = (props) => {
  // console.table(props);
  const { t } = useTranslation();
  const dataGridLocaleText = useDataGridLocaleText();
  const { customerId, customerNumber } = useContext(GlobalContext);
  const [loading, setLoading] = useState(false);
  const [allClaims, setAllClaims] = useState([]);

  // const { accounts, instance } = useMsal();
  // const [userId, setUserId] = useState();

  const [activeTab, setActiveTab] = useState(0); // 0 for 'All Claims' tab
  const [selectedCountry, setSelectedCountry] = useState("any");
  const [selectedYear, setSelectedYear] = useState("any");
  const [selectedStartMonth, setSelectedStartMonth] = useState("any");
  const [selectedEndMonth, setSelectedEndMonth] = useState("any");
  const [selectedClaimState, setSelectedClaimState] = useState("any");

  // Add this function to handle tab changes
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const [countryData, setCountryData] = useState([]);

  useEffect(() => {
    async function fetchReferenceData() {
      const countryResponse = await axios.get("/api/v1/countries");
      setCountryData(countryResponse.data);
    }
    fetchReferenceData();
  }, []);

  const handleCountryValue = (value, name) => {
    setSelectedCountry(value);
  };

  const handleYearValue = (value, name) => {
    setSelectedYear(value);
  };

  const handleStartMonthValue = (value, name) => {
    setSelectedStartMonth(value);
  };

  const handleEndMonthValue = (value, name) => {
    setSelectedEndMonth(value);
  };

  const handleClaimStateValue = (value, name) => {
    setSelectedClaimState(value);
  };

  const handleResetFilter = () => {
    setSelectedCountry("any");
    setSelectedYear("any");
    setSelectedStartMonth("any");
    setSelectedEndMonth("any");
    setSelectedClaimState("any");
  };

  function generateArrayOfYears() {
    var max = new Date().getFullYear() + 1;
    var min = max - 5;
    var years = [];
    for (var i = max; i >= min; i--) {
      years.push({ yearId: i, yearText: i });
    }
    return years;
  }

  let yearArr = generateArrayOfYears().reverse();

  const monthArr = [
    { monthId: 1, monthText: t("Jan") },
    { monthId: 2, monthText: t("Feb") },
    { monthId: 3, monthText: t("Mar") },
    { monthId: 4, monthText: t("Apr") },
    { monthId: 5, monthText: t("May") },
    { monthId: 6, monthText: t("Jun") },
    { monthId: 7, monthText: t("Jul") },
    { monthId: 8, monthText: t("Aug") },
    { monthId: 9, monthText: t("Sep") },
    { monthId: 10, monthText: t("Oct") },
    { monthId: 11, monthText: t("Nov") },
    { monthId: 12, monthText: t("Dec") },
  ];

  const enrichClaimRow = (claim) => {
    let displayText;
    if (claim.claimCode && String(claim.claimCode).slice(-2) === "11") {
      displayText = "Pre-Financed";
    } else {
      switch (claim.claimStateId) {
        case 1:
          displayText = "Collecting";
          break;
        case 3:
          displayText = "In progress";
          break;
        case 4:
          displayText = "Pending";
          break;
        case 16:
          if (claim.hasRejectedInvoices === 0) {
            displayText = "Finished";
          } else {
            displayText = "(Partly) Rejected";
          }
          break;
        default:
          displayText = "";
      }
    }

    return {
      ...claim,
      claimStateDescription: displayText,
      countryCode: claim.countryCode ?? claim.CountryCode ?? "",
      currencyCode: claim.currencyCode ?? claim.CurrencyCode ?? "",
      exchangeRate: claim.exchangeRate ?? claim.ExchangeRate ?? null,
      claimDate: claim.claimDate ? claim.claimDate.split("T")[0] : claim.claimDate,
      reimbursementDate: claim.reimbursementDate ? claim.reimbursementDate.split("T")[0] : claim.reimbursementDate,
      payDate: (() => {
        const value = claim.payDate ?? claim.PayDate;
        return value ? String(value).split("T")[0] : value;
      })(),
      paymentReceivedDate: (() => {
        const value = claim.paymentReceivedDate ?? claim.PaymentReceivedDate;
        return value ? String(value).split("T")[0] : value;
      })(),
    };
  };

  const fetchClaims = useCallback(async () => {
    if (!customerId) {
      return;
    }

    setLoading(true);

    try {
      const response = await axios.get("/api/v1/claimsByEntityId/" + customerId);
      setAllClaims(response.data.map(enrichClaimRow));
    } catch (error) {
      console.error("Error fetching claims by entity ID:", error);
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => {
    fetchClaims();
  }, [fetchClaims]);

  const tableData = useMemo(() => {
    let filteredClaims = allClaims;

    if (activeTab === 0) {
      filteredClaims = filteredClaims.filter((claim) => claim.claimStateId >= 4);
    } else if (activeTab === 1) {
      filteredClaims = filteredClaims.filter((claim) => claim.claimStateId === 4);
    } else if (activeTab === 2) {
      filteredClaims = filteredClaims.filter((claim) => {
        const isPreFinanced = claim.claimCode && claim.claimCode.slice(-2) === "11";
        return claim.claimStateId === 16 && claim.hasRejectedInvoices === 0 && !isPreFinanced;
      });
    } else if (activeTab === 3) {
      filteredClaims = filteredClaims.filter(
        (claim) => claim.claimCode && claim.claimCode.slice(-2) === "11" && claim.claimStateId >= 4
      );
    } else if (activeTab === 4) {
      filteredClaims = filteredClaims.filter((claim) => claim.claimStateId === 16 && claim.hasRejectedInvoices === 1);
    }

    if (selectedCountry !== "any") {
      filteredClaims = filteredClaims.filter((claim) => claim.countryId === selectedCountry);
    }
    if (selectedYear !== "any") {
      filteredClaims = filteredClaims.filter((claim) => claim.year === selectedYear);
    }
    if (selectedStartMonth !== "any") {
      filteredClaims = filteredClaims.filter((claim) => claim.startMonth === Number(selectedStartMonth));
    }
    if (selectedEndMonth !== "any") {
      filteredClaims = filteredClaims.filter((claim) => claim.endMonth === Number(selectedEndMonth));
    }
    if (selectedClaimState !== "any") {
      filteredClaims = filteredClaims.filter((claim) => {
        if (selectedClaimState === "pending") {
          return claim.claimStateId === 4;
        }
        if (selectedClaimState === "finished") {
          const isPreFinanced = claim.claimCode && claim.claimCode.slice(-2) === "11";
          return claim.claimStateId === 16 && claim.hasRejectedInvoices === 0 && !isPreFinanced;
        }
        if (selectedClaimState === "preFinanced") {
          return claim.claimCode && claim.claimCode.slice(-2) === "11" && claim.claimStateId >= 4;
        }
        if (selectedClaimState === "rejected") {
          return claim.claimStateId === 16 && claim.hasRejectedInvoices === 1;
        }
        return true;
      });
    }

    return filteredClaims;
  }, [
    allClaims,
    activeTab,
    selectedCountry,
    selectedYear,
    selectedStartMonth,
    selectedEndMonth,
    selectedClaimState,
  ]);

  // useEffect(() => {
  //   const fetchUserId = async () => {
  //     if (accounts.length > 0) {
  //       const activeAccount = accounts[0];
  //       if (activeAccount.idTokenClaims) {
  //         setUserId(activeAccount.idTokenClaims["oid"] || activeAccount.idTokenClaims["sub"]);
  //       }
  //     }
  //   };

  //   fetchUserId();
  // }, [accounts, instance]);

  // useEffect(() => {
  //   // Create an async function inside the useEffect
  //   // console.log(userId);
  //   const fetchUserData = async () => {
  //     try {
  //       if (!userId) {
  //         return;
  //       }

  //       // Await the asynchronous call to complete and get the response
  //       const response = await axios.get("/api/v1/entityByAzureId/" + userId);

  //       // Destructure the required fields from the response data
  //       const { entityId, entityNumber } = response.data;

  //       // Update your state with the response data
  //       setCustomerId(entityId);
  //       setCustomerNumber(entityNumber);
  //     } catch (error) {
  //       // Log or handle the error as needed
  //       console.error("Error fetching entity by Azure ID:", error);
  //     }
  //   };

  //   // Call the async function
  //   fetchUserData();
  // }, [userId]); // Dependency array to re-run the useEffect when userId changes

  // const fetchClaimsByEntityId = useCallback(() => {
  //   setLoading(true);
  //   axios
  //     .get("/api/v1/claimsByEntityId/" + customerId)
  //     .then((response) => {
  //       // console.log("temp" + response.data);
  //       // console.log(customerId);
  //       const tempData = response.data.filter((claim) => claim.claimStateId >= 1);
  //       setTableData(tempData);
  //     })
  //     .catch((error) => {
  //       console.error("Error fetching claims by entity ID:", error);
  //     })
  //     .finally(() => {
  //       setLoading(false);
  //     });
  // }, [customerId]);

  // useEffect(() => {
  //   fetchClaimsByEntityId();
  // }, [fetchClaimsByEntityId]);

  // console.table(tableData);

  const columns = [
    // {
    //   field: "claimId",
    //   headerName: "Claim ID",
    //   width: 50,
    //   hide: true,
    // },
    { field: "claimCode", headerName: t("Claim Code"), flex: 0.7 },
    { field: "year", headerName: t("Year"), flex: 0.3 },
    {
      field: "startMonth",
      headerName: t("Start Month"),
      flex: 0.4,
      valueFormatter: (params) => params.value.toString().padStart(2, "0"),
    },
    {
      field: "endMonth",
      headerName: t("End Month"),
      flex: 0.4,
      valueFormatter: (params) => params.value.toString().padStart(2, "0"),
    },
    {
      field: "countryCode",
      headerName: t("Country"),
      flex: 0.3,
      // valueGetter: (params) =>
      //   countryData.find(
      //     (country) => country.countryId === params.row.countryId
      //   ).countryCode,
    },
    {
      field: "claimStateDescription",
      headerName: t("Claim State"),
      flex: 0.5,
      valueFormatter: (params) => (params.value ? t(params.value) : ""),
      // renderCell: (params) => {
      //   let displayText = "";

      //   // Check if the claimStateId is 16 and if the row has any rejected invoices
      //   if (params.value === 16 && params.row.hasRejectedInvoices === 1) {
      //     displayText = "Partly rejected";
      //   } else {
      //     // Based on the claimStateId, set the display text
      //     switch (params.value) {
      //       case 1:
      //         displayText = "Collecting";
      //         break;
      //       case 3:
      //         displayText = "In progress";
      //         break;
      //       case 4:
      //         displayText = "Pending";
      //         break;
      //       case 16:
      //         displayText = "Finished";
      //         break;
      //       default:
      //         displayText = params.value; // or any other default text you want
      //     }
      //   }

      //   return <div>{displayText}</div>;
      // },
    },
    {
      field: "claimDateSortable",
      headerName: t("Claim Date"),
      flex: 0.4,
      sortingOrder: ["asc", "desc"],
      valueGetter: (params) => {
        const nonRealDates = [
          "0000-00-00",
          "01-01-1900",
          "01-01-1970",
          "00-00-0000",
          "01-01-0001",
          "0001-01-01",
          "0001-01-01T00:00:00",
        ];
        const { claimCode, claimDate } = params.row || {};
        if (!claimCode || !claimDate) return Number.MAX_SAFE_INTEGER;
        const lastTwoDigits = claimCode.slice(-2);
        if (["11", "10"].includes(lastTwoDigits) || nonRealDates.includes(claimDate)) {
          return Number.MAX_SAFE_INTEGER;
        }
        const ts = new Date(claimDate).getTime();
        return isNaN(ts) ? Number.MAX_SAFE_INTEGER : ts;
      },
      sortComparator: (a, b) => a - b,
      renderCell: (params) => {
        const nonRealDates = [
          "0000-00-00",
          "01-01-1900",
          "01-01-1970",
          "00-00-0000",
          "01-01-0001",
          "0001-01-01",
          "0001-01-01T00:00:00",
        ];
        const { claimCode, claimDate } = params.row || {};
        const lastTwoDigits = claimCode ? claimCode.slice(-2) : "";
        if (["11", "10"].includes(lastTwoDigits)) return t("N/A");
        if (nonRealDates.includes(claimDate)) return t("N/A");
        return formatDisplayDate(claimDate);
      },
    },
    {
      field: "reimbursementDateSortable",
      headerName: t("Refund Date"),
      flex: 0.6,
      sortingOrder: ["asc", "desc"],
      valueGetter: (params) => {
        const nonRealDates = [
          "0000-00-00",
          "01-01-1900",
          "01-01-1970",
          "00-00-0000",
          "01-01-0001",
          "0001-01-01",
          "0001-01-01T00:00:00",
        ];
        const { claimCode, reimbursementDate, notificationDate } = params.row || {};
        if (!claimCode || !reimbursementDate) return Number.MAX_SAFE_INTEGER;
        const lastTwoDigits = claimCode.slice(-2);
        if (["11", "01", "10"].includes(lastTwoDigits) || nonRealDates.includes(notificationDate)) {
          return Number.MAX_SAFE_INTEGER;
        }
        const ts = new Date(notificationDate).getTime();
        return isNaN(ts) ? Number.MAX_SAFE_INTEGER : ts;
      },
      sortComparator: (a, b) => a - b,
      renderCell: (params) => {
        const nonRealDates = [
          "0000-00-00",
          "01-01-1900",
          "01-01-1970",
          "00-00-0000",
          "01-01-0001",
          "0001-01-01",
          "0001-01-01T00:00:00",
        ];
        const { claimCode, notificationDate } = params.row || {};
        const lastTwoDigits = claimCode ? claimCode.slice(-2) : "";
        if (["01", "10", "11"].includes(lastTwoDigits)) return t("N/A");
        if (nonRealDates.includes(notificationDate)) return t("N/A");
        return formatDisplayDate(notificationDate);
      },
    },
    {
      field: "payDateSortable",
      headerName: t("Payout Date"),
      flex: 0.6,
      sortingOrder: ["asc", "desc"],
      valueGetter: (params) => getVatClaimPayoutDateSortValue(params.row),
      sortComparator: (a, b) => a - b,
      renderCell: (params) => {
        const resolved = resolveVatClaimPayoutDate(params.row);

        if (resolved.kind === "na") {
          const { claimCode } = params.row || {};

          if (claimCodeEndsWithZero(claimCode)) {
            const handleOpenPayoutDetails = (event) => {
              event.stopPropagation();
              setPayoutDetailsDialog({ isOpen: true, claim: params.row });
            };

            return (
              <Tooltip title={t("Click to enter payment received details")}>
                <Box
                  component="button"
                  type="button"
                  onClick={handleOpenPayoutDetails}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      handleOpenPayoutDetails(event);
                    }
                  }}
                  tabIndex={0}
                  aria-label={t("Enter payment received details for {{code}}", { code: claimCode })}
                  sx={{
                    border: 0,
                    background: "none",
                    cursor: "pointer",
                    color: "primary.main",
                    textDecoration: "underline",
                    p: 0,
                    font: "inherit",
                  }}
                >
                  {t("N/A")}
                </Box>
              </Tooltip>
            );
          }

          return t("N/A");
        }

        if (resolved.kind === "preFinanced") {
          return (
            <div style={{ display: "flex", alignItems: "center" }}>
              {t("Pre-Financed")}
              <Tooltip
                title={t(
                  "Due to pre-financing, the payout date is related to the individual invoices. Please look at the individual invoices to see when the payment took place."
                )}
                enterTouchDelay={0}
                placement="top"
              >
                <InfoIcon sx={{ color: "#376FD0", marginLeft: 1, fontSize: 16, cursor: "pointer" }} />
              </Tooltip>
            </div>
          );
        }

        return formatDisplayDate(resolved.value);
      },
    },
    // {
    //   field: "claimGrossAmount",
    //   headerName: "Gross Amount",
    //   flex: 0.5,
    //   align: "right",
    //   renderCell: (params) => (
    //     <div style={{ textAlign: "right", paddingRight: "15px" }}>
    //       {params.value.toLocaleString("nl-NL", {
    //         minimumFractionDigits: 2,
    //         maximumFractionDigits: 2,
    //       })}
    //     </div>
    //   ),
    // },
    {
      field: "currencyCode",
      headerName: t("Currency"),
      flex: 0.3,
    },
    {
      field: "claimVATAmountLocCur",
      headerName: t("VAT Local"),
      flex: 0.5,
      align: "right",
      renderCell: (params) => (
        <div style={{ textAlign: "right", paddingRight: "15px" }}>
          {params.value.toLocaleString("nl-NL", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}
        </div>
      ),
    },
    {
      field: "claimVATAmountEUR",
      headerName: t("VAT EUR"),
      description: t("+/- due to changing exchange rates"),
      flex: 0.5,
      align: "right",
      renderCell: (params) => {
        const exchangeRate = params.row.exchangeRate;
        const eurPrice = params.row.claimVATAmountEUR ?? params.value ?? 0;

        return (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", paddingRight: "15px" }}>
            {exchangeRate != null && exchangeRate !== 1 && (
              <Tooltip title={t("VAT EUR: +/- due to changing exchange rates")} enterTouchDelay={0} placement="top">
                <InfoIcon
                  sx={{
                    color: "#376FD0",
                    marginRight: 1,
                    fontSize: 16,
                    cursor: "pointer",
                  }}
                />
              </Tooltip>
            )}
            {eurPrice.toLocaleString("nl-NL", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </div>
        );
      },
    },
    {
      field: "actions",
      headerName: t("View"),
      flex: 0.5,
      renderCell: (params) => {
        return (
          <Box>
            <IconButton
              onClick={(e) => {
                setViewClaimDialog({
                  isOpen: true,
                  title: t("View Claim"),
                  params: params.row,
                  countryData: countryData,
                  custInfo: [{ customerId: customerId, customerNumber: customerNumber }],
                  subTitle: t("Are you sure you want to delete {{code}}?", { code: params.row.claimCode }),
                  onConfirm: () => {
                    onEditConfirmed(e, params.row);
                  },
                });
              }}
            >
              <VisibilityIcon />
            </IconButton>
          </Box>
        );
      },
    },
  ];

  const [notify, setNotify] = useState({
    isOpen: false,
    message: "",
    type: "",
  });

  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: "",
    subTitle: "",
  });

  const [viewClaimDialog, setViewClaimDialog] = useState({
    isOpen: false,
    title: "",
    subTitle: "",
    custInfo: [],
  });

  const [payoutDetailsDialog, setPayoutDetailsDialog] = useState({
    isOpen: false,
    claim: null,
  });

  function onEditConfirmed(e, params) {
    // console.log("start return params");
    // console.table(viewClaimDialog.test);
    // console.log("end return params");

    window.location.reload(false);

    setViewClaimDialog({ ...viewClaimDialog, isOpen: false });
  }

  // function onDeleteConfirmed(e, params) {
  //   // return false;
  //   // Delete Claim
  //   axios
  //     .delete("/api/v1/claims/" + params.claimId)
  //     .then((response) => {
  //       // console.log(response);

  //       axios
  //         .put(
  //           "/api/v1/customerInvoices/updateInvoiceRemoveClaimId/" +
  //             params.claimId
  //         )
  //         .then((response) => {
  //           // console.log(response);
  //         });

  //       setNotify({
  //         isOpen: true,
  //         message: "Claim deleted successfully",
  //         type: "success",
  //       });
  //       fetchClaimsByEntityId();
  //     })
  //     .catch((error) => {
  //       console.log(error);
  //       setNotify({
  //         isOpen: true,
  //         message: "Error deleting claim",
  //         type: "error",
  //       });
  //     })
  //     .finally(() => {
  //       setConfirmDialog({ ...confirmDialog, isOpen: false });
  //     });
  // }

  const [pageSize, setPageSize] = useState(15);

  const [selectedClaimCount, setSelectedClaimCount] = useState(0);
  const [selectedClaimTotalAmount, setSelectedClaimTotalAmount] = useState(0);
  const [selectedClaimTotalVATAmount, setSelectedClaimTotalVATAmount] = useState(0);
  // const [selectedRows, setSelectedRows] = useState([]);

  const handleSelectionChange = (newSelection) => {
    // setSelectedRows(newSelection);
    let selectedRowsAmount = 0;
    let selectedRowsVATAmount = 0;
    // let disablePreFinanced = false;

    newSelection.forEach((rowId) => {
      const selectedRow = tableData.find((row) => row.claimId === rowId);
      if (selectedRow) {
        selectedRowsAmount += selectedRow.claimVATAmountLocCur;
        selectedRowsVATAmount += selectedRow.claimVATAmountEUR ?? 0;
        // console.log(selectedRowsAmount);

        // Check if preFinancedId is greater than 0
        // if (selectedRow.preFinanceId > 0) {
        //   disablePreFinanced = true;
        // }
      }
    });

    setSelectedClaimCount(newSelection.length);
    setSelectedClaimTotalAmount(selectedRowsAmount);
    setSelectedClaimTotalVATAmount(selectedRowsVATAmount);

    // Update the button's disabled state
    // setIsPreFinancedDisabled(disablePreFinanced);
  };

  const BulkEditFooter = () => {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          borderTop: "1px solid #ccc",
          paddingTop: 10,
          paddingLeft: 10,
        }}
      >
        <div style={{ display: "flex", alignItems: "flex-start" }}>
          <div style={{ display: "flex", flexDirection: "column" }}>
            <div style={{ fontWeight: "bold", marginBottom: 5 }}>{t("Total Selected Claims")}:</div>
            <div style={{ marginBottom: 5 }}>{selectedClaimCount}</div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", marginLeft: 20 }}>
            <div style={{ fontWeight: "bold", marginBottom: 5 }}>{t("Total VAT Local")}:</div>
            <div style={{ marginBottom: 5 }}>
              {selectedClaimTotalAmount.toLocaleString("nl-NL", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", marginLeft: 20 }}>
            <div style={{ fontWeight: "bold", marginBottom: 5 }}>{t("Total VAT EUR")}:</div>
            <div style={{ marginBottom: 5 }}>
              {selectedClaimTotalVATAmount.toLocaleString("nl-NL", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </div>
          </div>
          {/* <div
            style={{ display: "flex", flexDirection: "column", marginLeft: 20 }}
          >
            <div style={{ fontWeight: "bold", marginBottom: 5 }}>
              Total VAT Amount EUR:
            </div>
            <div style={{ marginBottom: 5 }}>
              {selectedInvoiceTotalVATAmountEUR.toLocaleString("nl-NL", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </div>
          </div> */}
        </div>
        <div>
          <div>
            <GridFooter
              style={{ borderTop: "none" }}
              pageSize={pageSize}
              onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
              rowsPerPageOptions={[15, 50, 100]}
            />
          </div>
        </div>
      </div>
    );
  };

  return (
    <React.Fragment>
      <Helmet title={t("Claims")} />
      <Grid justifyContent="space-between" container spacing={10}>
        <Grid item>
          {/* <Typography
            variant="h3"
            gutterBottom
            display="inline"
            style={{ marginBottom: "20px" }} // Adjust the value as needed for space
          >
            List of Claims
          </Typography> */}

          {/* <Breadcrumbs aria-label="Breadcrumb" mt={2}>
            <Link component={NavLink} to="/">
              Dashboard
            </Link>
            <Link component={NavLink} to="/">
              Pages
            </Link>
            <Typography>Customers</Typography>
          </Breadcrumbs> */}
        </Grid>
      </Grid>
      <Tabs value={activeTab} onChange={handleTabChange} aria-label={t("Claim Tabs")} sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Tab label={t("All Claims")} sx={{ typography: "h6", fontWeight: "bold" }} />
        <Tab label={t("Pending Claims")} sx={{ typography: "h6", fontWeight: "bold" }} />
        <Tab label={t("Finished Claims")} sx={{ typography: "h6", fontWeight: "bold" }} />
        <Tab label={t("Pre-Financed Claims")} sx={{ typography: "h6", fontWeight: "bold" }} />
        <Tab label={t("(Partly) Rejected Claims")} sx={{ typography: "h6", fontWeight: "bold" }} />
      </Tabs>
      {/* <Divider my={6} /> */}
      <Card mb={6}>
        {/* <AddClaimForm
          customerId={customerId}
          customerNumber={customerNumber}
          refreshTable={fetchClaimsByEntityId}
        /> */}
        <Notification notify={notify} setNotify={setNotify} />
        <Divider />
        <Grid container spacing={2} mt={0}>
          <ConfirmDialog confirmDialog={confirmDialog} setConfirmDialog={setConfirmDialog} />
          <ViewClaimDialog
            viewClaimDialog={viewClaimDialog}
            setViewClaimDialog={setViewClaimDialog}
            refreshTable={fetchClaims}
          />
          <ClaimPayoutDetailsDialog
            dialogState={payoutDetailsDialog}
            setDialogState={setPayoutDetailsDialog}
            onSaved={fetchClaims}
          />
          <Grid container xs={12} spacing={3} sx={{ pr: 2, mt: 2, ml: 2, mb: -2 }}>
            <Grid item xs={2}>
              <SelectWrapper
                name="countryId"
                labelColor="#f64435"
                label={t("Country")}
                options={[
                  { key: "any", value: t("Any") },
                  ...JSON.parse(
                    JSON.stringify(
                      JSON.parse(
                        JSON.stringify(countryData.filter((country) => Number(country.countryId) < 50)).split('"countryId":').join('"key":')
                      )
                    )
                      .split('"countryCode":')
                      .join('"value":')
                  ),
                ]}
                value={selectedCountry}
                handleChange={handleCountryValue}
              />
            </Grid>
            <Grid item xs={2}>
              <SelectWrapper
                name="year"
                label={t("Year")}
                options={[
                  { key: "any", value: t("Any") },
                  ...JSON.parse(
                    JSON.stringify(JSON.parse(JSON.stringify(yearArr).split('"yearId":').join('"key":')))
                      .split('"yearText":')
                      .join('"value":')
                  ),
                ]}
                value={selectedYear}
                handleChange={handleYearValue}
              />
            </Grid>
            <Grid item xs={2}>
              <SelectWrapper
                name="startMonth"
                label={t("Start month")}
                options={[
                  { key: "any", value: t("Any") },
                  ...JSON.parse(
                    JSON.stringify(JSON.parse(JSON.stringify(monthArr).split('"monthId":').join('"key":')))
                      .split('"monthText":')
                      .join('"value":')
                  ),
                ]}
                value={selectedStartMonth}
                handleChange={handleStartMonthValue}
              />
            </Grid>
            <Grid item xs={2}>
              <SelectWrapper
                name="endMonth"
                label={t("End month")}
                options={[
                  { key: "any", value: t("Any") },
                  ...JSON.parse(
                    JSON.stringify(JSON.parse(JSON.stringify(monthArr).split('"monthId":').join('"key":')))
                      .split('"monthText":')
                      .join('"value":')
                  ),
                ]}
                value={selectedEndMonth}
                handleChange={handleEndMonthValue}
              />
            </Grid>
            <Grid item xs={2}>
              <SelectWrapper
                name="claimState"
                label={t("Claim State")}
                options={[
                  { key: "any", value: t("Any") },
                  { key: "pending", value: t("Pending") },
                  { key: "finished", value: t("Finished") },
                  { key: "preFinanced", value: t("Pre-Financed") },
                  { key: "rejected", value: t("(Partly) Rejected") },
                ]}
                value={selectedClaimState}
                handleChange={handleClaimStateValue}
              />
            </Grid>
            <Grid item xs={2}>
              <Button onClick={handleResetFilter} sx={{ mt: 4, ml: 4, pl: 6, pr: 6 }} variant="outlined">
                {t("Reset")}
              </Button>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <DataGridExport tableData={tableData} columns={columns} exportType="claims" />
            </div>
            {loading ? (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "600px",
                }}
              >
                <Typography sx={{ mr: 6, fontSize: 20, color: "text.primary" }}>{t("Loading")}</Typography>
                <CircularProgress />
              </div>
            ) : (
                <DataGrid
                  checkboxSelection
                  idProperty="id"
                  editMode="cell"
                  getRowId={(row) => row.claimId}
                  localeText={dataGridLocaleText}
                  disableSelectionOnClick
                  disableColumnSelector
                  components={{
                    NoRowsOverlay: () => (
                      <Stack height="100%" alignItems="center" justifyContent="center">
                        {t("No Claims Found")}
                      </Stack>
                    ),
                    Toolbar: GridToolbar,
                    Footer: BulkEditFooter,
                  }}
                  density="compact"
                  componentsProps={{
                    toolbar: {
                      csvOptions: { disableToolbarButton: true },
                      printOptions: { disableToolbarButton: true },
                      showQuickFilter: true,
                      quickFilterProps: { debounceMs: 250 },
                    },
                  }}
                  onSelectionModelChange={handleSelectionChange}
                  rows={tableData}
                  columns={columns}
                  pageSize={pageSize}
                  onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
                  rowsPerPageOptions={[15, 50, 100]}
                  sx={{
                    height: 667,
                    width: "100%",
                    borderRadius: 1,
                    backgroundColor: "background.paper",
                    boxShadow: 2,
                    "& .MuiDataGrid-cell:hover": {
                      color: "primary.main",
                    },
                    "& .MuiTablePagination-root": {
                      color: "text.primary",
                    },
                    "& .MuiTablePagination-selectLabel": {
                      color: "text.primary",
                    },
                    "& .MuiTablePagination-displayedRows": {
                      color: "text.primary",
                    },
                    "& .MuiTablePagination-select": {
                      color: "text.primary",
                    },
                    "& .MuiTablePagination-selectIcon": {
                      color: "text.primary",
                    },
                    "& .MuiTablePagination-actions button": {
                      color: "text.primary",
                    },
                    marginTop: 0,
                  }}
                />
            )}
          </Grid>
        </Grid>
      </Card>
    </React.Fragment>
  );
};

export default CustomerClaims;
